import { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChoiceSelect } from '@/components/ui/choice-select';
import { monthLabel, monthOf } from '../list/list-query';
import { listReports } from '../reports/api';
import { NewReportDialog } from '../reports/NewReportDialog';
import type { NoteType, ReportSummary } from '../reports/types';

const NEW = '__new__';
const LOADING = { status: 'loading' } as const;

type Drafts =
  { status: 'loading' } | { status: 'failed' } | { status: 'ready'; items: ReportSummary[] };

/**
 * The month an expense dated `date` («2026-10-14») belongs to. Until a date is typed: the month of
 * the note the form was opened from (`fallback`, #946), else today's.
 */
export function reportMonth(date: string, fallback?: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date.slice(0, 7);
  return fallback ?? monthOf(new Date());
}

/**
 * The owner's draft reports of `month` or any later one, the nearest first (#946): an expense goes
 * in a note of its month or a later one, so a ticket bought today for a May trip finds May's note.
 * `known` is false when whose drafts is unknowable. A list read for another owner or month counts
 * as loading, so a choice is never checked against it (an owner and a report restored together,
 * U10). While `waiting`, nothing is read: the list stays loading, so a note chosen beforehand is
 * kept.
 */
function useDraftReports(month: string, ownerUserId: string, known: boolean, waiting: boolean) {
  const query = `${month}|${ownerUserId}|${String(known)}|${String(waiting)}`;
  const [state, setState] = useState<{ query: string; drafts: Drafts }>({
    query: '',
    drafts: { status: 'loading' },
  });
  useEffect(() => {
    let live = true;
    if (waiting) return;
    if (!known) {
      setState({ query, drafts: { status: 'ready', items: [] } });
      return;
    }
    void listReports({
      status: ['draft'],
      sort: 'period',
      direction: 'asc',
      limit: 100,
      ...(ownerUserId === '' ? {} : { owner: ownerUserId }),
    }).then((outcome) => {
      if (!live) return;
      setState({
        query,
        drafts: outcome.ok
          ? {
              status: 'ready',
              // The server sorts by month; an earlier month's note cannot take the expense.
              items: outcome.value.items.filter((item) => item.periodMonth >= month),
            }
          : { status: 'failed' },
      });
    });
    return () => {
      live = false;
    };
  }, [query, month, ownerUserId, known, waiting]);
  const drafts: Drafts = state.query === query && !waiting ? state.drafts : LOADING;
  const setDrafts = useCallback(
    (update: (current: Drafts) => Drafts) =>
      setState((current) => ({ ...current, drafts: update(current.drafts) })),
    [],
  );
  return [drafts, setDrafts] as const;
}

/**
 * «Nota» (spec §1.9): the report the new expense is filed in. It lists the person's draft reports
 * of the expense date's month and every later one, the nearest first, each with its month (#946),
 * then «Nessuna nota», and «Nuova nota…» for the date's month, which asks only for the «Motivo» and
 * creates a draft. A choice the date no longer allows is cleared.
 *
 * `ownerUserId` is the chosen owner (Gestione's «Per conto di»); with none chosen, a manager's own
 * drafts are read by `selfUserId`, and without it only «Nuova nota…» is offered.
 *
 * Opened from a note (#946), the page sets `waiting` until it knows the note's owner, so the note it
 * opened from stays chosen, and passes that note's month as `fallbackMonth` for «Nuova nota…» until
 * a date is typed.
 */
export function ReportField({
  date,
  ownerUserId,
  manages,
  value,
  onChange,
  selfUserId,
  fallbackMonth,
  waiting = false,
}: {
  readonly date: string;
  readonly ownerUserId: string;
  readonly manages: boolean;
  readonly value: string;
  readonly selfUserId?: string | undefined;
  readonly fallbackMonth?: string | undefined;
  readonly waiting?: boolean | undefined;
  /** `noteType` is the chosen note's, so the form offers only the categories it accepts. */
  readonly onChange: (reportId: string, noteType?: NoteType | null) => void;
}) {
  const { t, i18n } = useTranslation('expenses');
  const id = useId();
  const month = reportMonth(date, fallbackMonth);
  const listOwner = ownerUserId !== '' || !manages ? ownerUserId : (selfUserId ?? '');
  const ownDraftsKnown = !manages || listOwner !== '';
  const [drafts, setDrafts] = useDraftReports(month, listOwner, ownDraftsKnown, waiting);
  const [creating, setCreating] = useState(false);

  const items = drafts.status === 'ready' ? drafts.items : [];
  useEffect(() => {
    if (drafts.status !== 'ready' || value === '') return;
    const chosen = items.find((item) => item.id === value);
    // A note chosen from outside («Nuova spesa» on a report) tells the page its type once known.
    if (chosen === undefined) onChange('');
    else onChange(value, chosen.noteType ?? null);
  }, [drafts, items, value, onChange]);

  const chosenType = items.find((item) => item.id === value)?.noteType ?? null;
  const label = monthLabel(month, i18n.resolvedLanguage);
  return (
    <div className="space-y-1" data-field="report">
      <label htmlFor={id} className="text-sm font-semibold">
        {t('capture.report.label')}
      </label>
      <ChoiceSelect
        id={id}
        value={value}
        disabled={drafts.status === 'loading'}
        onChange={(next) =>
          next === NEW
            ? setCreating(true)
            : onChange(next, items.find((item) => item.id === next)?.noteType ?? null)
        }
        choices={[
          { value: '', label: t('capture.report.none') },
          ...items.map((item) => ({
            value: item.id,
            label: `${item.title} · ${item.number} · ${monthLabel(item.periodMonth, i18n.resolvedLanguage)}`,
          })),
          { value: NEW, label: t('capture.report.new', { period: label }) },
        ]}
      />
      {chosenType ? (
        <p role="status" className="text-sm text-muted-foreground" data-testid="report-type-hint">
          {t('capture.report.typeHint', { type: t(`reports.noteType.${chosenType}`) })}
        </p>
      ) : null}
      {drafts.status === 'failed' ? (
        <p role="status" className="text-sm text-muted-foreground">
          {t('capture.report.failed')}
        </p>
      ) : null}
      <NewReportDialog
        open={creating}
        period={month}
        ownerUserId={ownerUserId || undefined}
        onClose={() => setCreating(false)}
        onCreated={(report) => {
          setCreating(false);
          setDrafts((current) =>
            current.status === 'ready'
              ? { status: 'ready', items: [report, ...current.items] }
              : current,
          );
          onChange(report.id, report.noteType ?? null);
        }}
      />
    </div>
  );
}
